<template>
  <Dropdown
    v-bind="$attrs"
    v-model="inputValue"
    :options="countries"
    :placeholder="t('select_country')"
    :error="error"
    filterable
    :test-id="name || testId"
    :readonly="readonly"
  >
    <template #prefix>
      <Flag v-if="inputValue" :country-code="inputValue" />
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
  import { RouteName } from "~/types/routes";
  import { computed, ref } from "vue";

  const { t } = useI18n();
  const _modelValue = defineModel<string>();

  const props = withDefaults(
    defineProps<{
      error?: string;
      name?: string;
      selectDefault?: boolean;
      withAll?: boolean;
      testId?: string;
      type?: "default" | "consignments" | "returns";
      readonly?: boolean;
    }>(),
    {
      error: undefined,
      name: "",
      selectDefault: false,
      testId: "country-select",
      type: "default",
      readonly: false,
    }
  );

  onMounted(() => {
    if (props.selectDefault) {
      inputValue.value = "DK";
    }
  });

  const { value: inputValue, errorMessage } = useField(() => props.name, undefined, {
    syncVModel: props.name ? false : true,
    controlled: !!props.name,
  });

  const error = computed(() => {
    return props.error || errorMessage.value;
  });

  const input = ref<HTMLElement | null>(null);

  const el = computed(() => {
    return input.value?.input;
  });

  const countries = ref<DropdownOption[]>([]);

  const getCountries = () => {
    const param = useHasPageAccess(RouteName.consignments)
      ? "?is_consignment=1"
      : props.type === "returns"
        ? "?return=1"
        : "";
    return homeFetch("available-countries" + param)
      .then((res) => {
        if (!res.data) {
          return;
        }
        countries.value = formatCountriesList(res.data);
      })
      .finally(() => {
        if (props.withAll) {
          countries.value.unshift({
            label: t("all"),
            value: "",
          });
        }

        if (!countries.value.find((p) => p.value === inputValue.value)) {
          inputValue.value = "";
        }
      });
  };

  onMounted(() => {
    getCountries().then(() => {
      if (props.selectDefault) {
        inputValue.value = countries.value[0].value;
      }
    });
  });

  defineExpose({
    el,
  });
</script>
